import { useEffect } from "react";

import { msalInstance } from "@/config/msal-config";
import { readSharedAccount, writeSharedAccount } from "@/utils/authSync";

const POLL_INTERVAL_MS = 30_000;

/**
 * Per-tab, so a tab that cannot reconcile itself gives up instead of
 * reload-looping. Cleared as soon as a check agrees with the cookie.
 */
const RECOVERY_GUARD_KEY = "mj_auth_sync_recovered";

/**
 * Resets this app when a sibling app has signed out or signed a different
 * account in — see utils/authSync for why the two apps can disagree at all.
 *
 * Checked on mount, whenever the tab regains focus or visibility, and on a poll
 * so a tab left open in the background notices too. Reading `document.cookie` is
 * a string read, so polling costs nothing.
 */
export const useAuthSync = () => {
  useEffect(() => {
    let unmounted = false;

    const reset = async () => {
      window.sessionStorage.setItem(RECOVERY_GUARD_KEY, "1");

      // MSAL's cache is the only token store (see utils/auth.ts), so clearing
      // it is the whole reset.
      try {
        await msalInstance.clearCache();
      } catch {
        // The reload re-runs authentication either way — a failed cache clear
        // must not stop it.
      }

      if (!unmounted) window.location.reload();
    };

    const check = () => {
      const account =
        msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
      if (!account) return;

      const shared = readSharedAccount();

      // No cookie yet: first load after this shipped, or it expired. Adopt the
      // account this app already holds rather than reading it as a logout.
      if (shared === null) {
        writeSharedAccount(account.homeAccountId);
        return;
      }

      if (shared === account.homeAccountId) {
        // Rewrite to extend max-age while the app is in use, so an active
        // session never falls back to the ambiguous "no cookie" case above.
        writeSharedAccount(account.homeAccountId);
        window.sessionStorage.removeItem(RECOVERY_GUARD_KEY);
        return;
      }

      if (window.sessionStorage.getItem(RECOVERY_GUARD_KEY)) {
        // Already cleared and re-authenticated once in this tab and still
        // disagree — stop, rather than reloading forever.
        console.warn(
          `[authSync] account still out of sync after recovery (shared: ${shared})`
        );
        return;
      }

      void reset();
    };

    const checkIfVisible = () => {
      if (document.visibilityState === "visible") check();
    };

    check();
    const interval = window.setInterval(check, POLL_INTERVAL_MS);
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", checkIfVisible);

    return () => {
      unmounted = true;
      window.clearInterval(interval);
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", checkIfVisible);
    };
  }, []);
};
